// ═══════════════════════════════════════════════════════════════
// PROSPECT IMPORT MODAL — CSV Import for Acquisition Campaigns
// Path: src/components/campaigns/ProspectImportModal.jsx
//
// Creates new donors as "prospect" pathway and adds them as
// campaign recipients. For acquisition campaigns where recipients
// are not yet in the system.
//
// Expected CSV fields:
//   FullName, BusinessName, Contact, AddressLine1,
//   AddressLine2, City, State, Zip
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from "react";
import {
  Upload,
  X,
  Check,
  AlertTriangle,
  FileText,
  ArrowRight,
  UserPlus,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { parseCSV } from "../../services/importService";
import { campaignService } from "../../services/campaignService";
import { supabase } from "../../lib/supabase";
import { toSnakeCase } from "../../utils/fieldMapper";
import { debugLog } from "../../utils/debugLog";

// ── Field definitions for prospect import ────────────────────────

const PROSPECT_FIELDS = [
  { id: "fullName", label: "Full Name", required: true },
  { id: "businessName", label: "Business Name", required: false },
  { id: "contactName", label: "Contact Name", required: false },
  { id: "addressLine1", label: "Address Line 1", required: false },
  { id: "addressLine2", label: "Address Line 2", required: false },
  { id: "city", label: "City", required: false },
  { id: "state", label: "State", required: false },
  { id: "zip", label: "Zip", required: false },
];

// ── Name splitter ────────────────────────────────────────────────

const splitFullName = (fullName) => {
  if (!fullName || !fullName.trim()) return { firstName: "", lastName: "" };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts.pop();
  return { firstName: parts.join(" "), lastName };
};


// ═══════════════════════════════════════════════════════════════

export const ProspectImportModal = ({ campaignId, campaignName, onClose, onComplete }) => {
  const { palette, members, showMessage } = useApp();

  const [step, setStep] = useState(1);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [fileName, setFileName] = useState("");

  // Step 2: column mapping
  const [mapping, setMapping] = useState({});

  // Step 3: duplicate check + per-row skip/import
  const [skipSet, setSkipSet] = useState(new Set()); // indices to skip

  // Step 4: import progress
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [importResult, setImportResult] = useState(null);

  // ── Step 1: File upload ─────────────────────────────────────
  const handleFile = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setCsvRows(rows);

      // Auto-map columns by fuzzy header matching
      const autoMap = {};
      const headerLower = headers.map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ""));
      const matches = [
        { field: "fullName", patterns: ["fullname", "name", "recipientname", "donorname"] },
        { field: "businessName", patterns: ["businessname", "business", "company", "organization", "org"] },
        { field: "contactName", patterns: ["contactname", "contact", "contactperson", "primarycontact"] },
        { field: "addressLine1", patterns: ["addressline1", "address1", "address", "street", "streetaddress"] },
        { field: "addressLine2", patterns: ["addressline2", "address2", "apt", "suite", "unit"] },
        { field: "city", patterns: ["city", "town"] },
        { field: "state", patterns: ["state", "province", "st"] },
        { field: "zip", patterns: ["zip", "zipcode", "postalcode", "postal"] },
      ];
      matches.forEach(({ field, patterns }) => {
        const idx = headerLower.findIndex((h) => patterns.includes(h));
        if (idx !== -1) autoMap[field] = headers[idx];
      });
      setMapping(autoMap);
      setStep(2);
    };
    reader.readAsText(file);
  }, []);

  // ── Step 2 → 3: Preview data ───────────────────────────────
  const parsedProspects = csvRows.map((row) => {
    const fullName = row[mapping.fullName] || "";
    const contactName = row[mapping.contactName] || "";
    // Contact name is the person at the org; FullName is a fallback
    const person = contactName || fullName;
    const { firstName, lastName } = splitFullName(person);
    return {
      firstName,
      lastName,
      organization: row[mapping.businessName] || fullName || "",
      homeStreet: [row[mapping.addressLine1], row[mapping.addressLine2]]
        .filter(Boolean)
        .join(", ") || "",
      homeCity: row[mapping.city] || "",
      homeState: row[mapping.state] || "",
      homeZip: row[mapping.zip] || "",
      donorType: "organization",
      pathway: "prospect",
      notes: `Imported from campaign: ${campaignName || "Unknown"}`,
    };
  });

  const validProspects = parsedProspects.filter(
    (p) => p.firstName || p.lastName || p.organization
  );
  const invalidCount = parsedProspects.length - validProspects.length;

  // ── Duplicate detection by AddressLine1 ─────────────────────
  // Build a normalized lookup of existing member addresses
  const existingAddresses = React.useMemo(() => {
    const map = {};
    members.forEach((m) => {
      const addr = (m.homeStreet || "").trim().toLowerCase();
      if (addr.length >= 3) {
        if (!map[addr]) map[addr] = [];
        map[addr].push(m);
      }
    });
    return map;
  }, [members]);

  // Tag each valid prospect with duplicate info
  const taggedProspects = React.useMemo(() => {
    return validProspects.map((p, idx) => {
      const addr = (p.homeStreet || "").trim().toLowerCase();
      const matches = addr.length >= 3 ? existingAddresses[addr] || [] : [];
      return { ...p, _idx: idx, _duplicates: matches };
    });
  }, [validProspects, existingAddresses]);

  const duplicateCount = taggedProspects.filter((p) => p._duplicates.length > 0).length;
  const importList = taggedProspects.filter((p) => !skipSet.has(p._idx));

  // Pre-populate skipSet with duplicates when entering step 3
  const handleGoToReview = () => {
    const dupeSkips = new Set();
    taggedProspects.forEach((p) => {
      if (p._duplicates.length > 0) dupeSkips.add(p._idx);
    });
    setSkipSet(dupeSkips);
    setStep(3);
  };

  const toggleSkip = (idx) => {
    setSkipSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // ── Step 4: Execute import (batch) ───────────────────────────
  const handleImport = async () => {
    const toCreate = importList.map(({ _idx, _duplicates, ...data }) => data);
    const skippedWithDupes = taggedProspects.filter(
      (p) => skipSet.has(p._idx) && p._duplicates.length > 0
    );

    if (toCreate.length === 0 && skippedWithDupes.length === 0) return;
    setImporting(true);
    setProgress({ done: 0, total: 3 }); // 3 phases: insert, collect IDs, link

    const allRecipientIds = [];
    let created = 0;
    let linkedExisting = 0;
    let errors = 0;

    // Phase 1: Bulk insert new prospects in chunks of 50
    const CHUNK_SIZE = 50;
    for (let i = 0; i < toCreate.length; i += CHUNK_SIZE) {
      const chunk = toCreate.slice(i, i + CHUNK_SIZE);
      try {
        const { data, error } = await supabase
          .from("members")
          .insert(chunk.map((row) => toSnakeCase(row)))
          .select("id");

        if (error) {
          debugLog.error("Prospect bulk insert", error);
          errors += chunk.length;
        } else if (data) {
          data.forEach((row) => allRecipientIds.push(row.id));
          created += data.length;
        }
      } catch (err) {
        debugLog.error("Prospect bulk insert exception", err);
        errors += chunk.length;
      }
      setProgress({
        done: Math.min(created + errors, toCreate.length),
        total: toCreate.length,
      });
    }

    // Phase 2: Collect existing donor IDs for skipped duplicates
    skippedWithDupes.forEach((p) => {
      allRecipientIds.push(p._duplicates[0].id);
      linkedExisting++;
    });

    // Phase 3: Link all IDs (new + existing) as campaign recipients
    let recipientCount = 0;
    if (allRecipientIds.length > 0) {
      const unique = [...new Set(allRecipientIds)];
      try {
        recipientCount = await campaignService.addRecipients(
          campaignId,
          unique,
          "prospect_import"
        );
      } catch (err) {
        showMessage("Some recipients failed to link to campaign", "warning");
      }
    }

    setImportResult({
      created,
      linkedExisting,
      recipientTotal: recipientCount,
      errors,
      skippedInvalid: invalidCount,
    });
    setStep(4);
    setImporting(false);

    showMessage(
      `${created} prospects created, ${linkedExisting} existing donors linked — ${recipientCount} total recipients`
    );
  };

  // ── Styles ──────────────────────────────────────────────────
  const selectStyle = {
    flex: 1,
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
  };

  const btnPrimary = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 20px",
    borderRadius: 8,
    background: palette.accent1,
    color: "#fff",
    fontSize: FONT_SIZES.xs,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    border: "none",
    cursor: "pointer",
  };

  const btnSecondary = {
    padding: "8px 16px",
    borderRadius: 8,
    border: `1px solid ${palette.inputBorder}`,
    background: "transparent",
    color: palette.text2,
    fontSize: FONT_SIZES.xs,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    cursor: "pointer",
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1000,
          background: "rgba(0,0,0,0.4)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 640,
          maxHeight: "85vh",
          zIndex: 1001,
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UserPlus size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1 }}>
              Import Prospects — Step {step} of 4
            </span>
          </div>
          <button
            onClick={step === 4 ? onComplete : onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}
          >
            <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>

          {/* ── STEP 1: Upload ─────────────────────────────── */}
          {step === 1 && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <FileText size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 16 }} />
              <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 600, color: palette.text1, marginBottom: 8 }}>
                Import Prospects for Acquisition Campaign
              </div>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
                Upload a CSV with your mailing list. New donors will be created as
                prospects and automatically linked to this campaign.
              </div>
              <div
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  marginBottom: 20,
                  padding: "10px 16px",
                  borderRadius: 8,
                  background: `${palette.accent1}08`,
                  border: `1px solid ${palette.accent1}15`,
                  textAlign: "left",
                  lineHeight: 1.6,
                }}
              >
                <strong>Expected columns:</strong> FullName, BusinessName, Contact,
                AddressLine1, AddressLine2, City, State, Zip
              </div>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "10px 20px",
                  borderRadius: 8,
                  background: palette.accent1,
                  color: "#fff",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Upload size={ICON_SIZES.sm} />
                Choose CSV File
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFile}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          )}

          {/* ── STEP 2: Map Columns ────────────────────────── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}>
                <strong>{fileName}</strong> — {csvRows.length} rows, {csvHeaders.length} columns
              </div>

              {/* Column mapping */}
              <div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 8 }}>
                  Map CSV columns to prospect fields
                </div>
                {PROSPECT_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 140,
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                        flexShrink: 0,
                      }}
                    >
                      {field.label}
                      {field.required && (
                        <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
                      )}
                    </span>
                    <ArrowRight size={ICON_SIZES.xs} style={{ color: palette.text3, flexShrink: 0 }} />
                    <select
                      value={mapping[field.id] || ""}
                      onChange={(e) =>
                        setMapping((prev) => ({ ...prev, [field.id]: e.target.value }))
                      }
                      style={selectStyle}
                    >
                      <option value="">— Skip —</option>
                      {csvHeaders.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* CSV preview */}
              <div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 4 }}>
                  Preview (first 5 rows)
                </div>
                <div style={{ overflowX: "auto", border: `1px solid ${palette.cardBorder}`, borderRadius: 6 }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "0.55rem", fontFamily: FONT_FAMILY, width: "100%" }}>
                    <thead>
                      <tr>
                        {csvHeaders.map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "6px 10px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: mapping[Object.keys(mapping).find((k) => mapping[k] === h)]
                                ? palette.accent1
                                : palette.text2,
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 5).map((row, i) => (
                        <tr key={i}>
                          {csvHeaders.map((h) => (
                            <td
                              key={h}
                              style={{
                                padding: "4px 10px",
                                color: palette.text1,
                                borderBottom: `1px solid ${palette.cardBorder}`,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row[h] || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(1)} style={btnSecondary}>
                  Back
                </button>
                <button
                  onClick={handleGoToReview}
                  disabled={!mapping.fullName}
                  style={{
                    ...btnPrimary,
                    opacity: mapping.fullName ? 1 : 0.5,
                    cursor: mapping.fullName ? "pointer" : "default",
                  }}
                >
                  Review Prospects
                  <ArrowRight size={ICON_SIZES.sm} />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Review + Duplicate Check ─────────── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Summary tiles */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1, padding: 14, borderRadius: 8, background: "#10b98110", textAlign: "center" }}>
                  <UserPlus size={ICON_SIZES.md} style={{ color: "#10b981", marginBottom: 4 }} />
                  <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: "#10b981" }}>
                    {importList.length}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Will import</div>
                </div>
                {duplicateCount > 0 && (
                  <div style={{ flex: 1, padding: 14, borderRadius: 8, background: "#f59e0b10", textAlign: "center" }}>
                    <AlertTriangle size={ICON_SIZES.md} style={{ color: "#f59e0b", marginBottom: 4 }} />
                    <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: "#f59e0b" }}>
                      {duplicateCount}
                    </div>
                    <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Duplicates found</div>
                  </div>
                )}
                <div style={{ flex: 1, padding: 14, borderRadius: 8, background: `${palette.text3}10`, textAlign: "center" }}>
                  <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text3 }}>
                    {skipSet.size}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Skipping</div>
                </div>
              </div>

              {duplicateCount > 0 && (
                <div style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  fontSize: FONT_SIZES.xs,
                  color: "#92400e",
                  lineHeight: 1.4,
                }}>
                  <strong>{duplicateCount} records</strong> match an existing donor by address.
                  Duplicates are set to skip by default — uncheck "Skip" to import anyway.
                </div>
              )}

              {/* Prospect list with skip toggles */}
              <div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, marginBottom: 4 }}>
                  All prospects ({taggedProspects.length})
                </div>
                <div style={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${palette.cardBorder}`, borderRadius: 6 }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "0.55rem", fontFamily: FONT_FAMILY, width: "100%" }}>
                    <thead>
                      <tr>
                        {["", "Contact", "Organization", "Address", "City", "Status"].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "6px 8px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: palette.text2,
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              whiteSpace: "nowrap",
                              position: "sticky",
                              top: 0,
                              background: palette.cardBg,
                              zIndex: 1,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {taggedProspects.map((p) => {
                        const isDupe = p._duplicates.length > 0;
                        const isSkipped = skipSet.has(p._idx);
                        const matchName = isDupe
                          ? `${p._duplicates[0].firstName || ""} ${p._duplicates[0].lastName || ""}`.trim() ||
                            p._duplicates[0].organization || "Existing donor"
                          : null;
                        return (
                          <tr
                            key={p._idx}
                            style={{
                              opacity: isSkipped ? 0.45 : 1,
                              background: isDupe && !isSkipped ? "#fef3c710" : "transparent",
                            }}
                          >
                            <td style={{ padding: "4px 8px", borderBottom: `1px solid ${palette.cardBorder}`, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={isSkipped}
                                onChange={() => toggleSkip(p._idx)}
                                title={isSkipped ? "Click to import this record" : "Click to skip this record"}
                                style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#f59e0b" }}
                              />
                            </td>
                            <td style={{ padding: "4px 8px", color: palette.text1, borderBottom: `1px solid ${palette.cardBorder}`, whiteSpace: "nowrap" }}>
                              {p.firstName} {p.lastName}
                            </td>
                            <td style={{ padding: "4px 8px", color: palette.text1, borderBottom: `1px solid ${palette.cardBorder}`, whiteSpace: "nowrap" }}>
                              {p.organization || "—"}
                            </td>
                            <td style={{ padding: "4px 8px", color: palette.text1, borderBottom: `1px solid ${palette.cardBorder}`, whiteSpace: "nowrap" }}>
                              {p.homeStreet || "—"}
                            </td>
                            <td style={{ padding: "4px 8px", color: palette.text1, borderBottom: `1px solid ${palette.cardBorder}`, whiteSpace: "nowrap" }}>
                              {p.homeCity || "—"}
                            </td>
                            <td style={{ padding: "4px 8px", borderBottom: `1px solid ${palette.cardBorder}`, whiteSpace: "nowrap" }}>
                              {isDupe ? (
                                <span style={{
                                  fontSize: "0.5rem",
                                  fontWeight: 600,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: isSkipped ? "#fef3c7" : "#fde68a",
                                  color: "#92400e",
                                }}>
                                  Duplicate — {matchName}
                                </span>
                              ) : isSkipped ? (
                                <span style={{ fontSize: "0.5rem", color: palette.text3 }}>Skipping</span>
                              ) : (
                                <span style={{
                                  fontSize: "0.5rem",
                                  fontWeight: 600,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: "#dcfce7",
                                  color: "#166534",
                                }}>
                                  New
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Progress bar (during import) */}
              {importing && (
                <div>
                  <div style={{
                    height: 6,
                    borderRadius: 3,
                    background: palette.cardBorder,
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%",
                      width: `${(progress.done / progress.total) * 100}%`,
                      background: palette.accent1,
                      borderRadius: 3,
                      transition: "width 0.2s",
                    }} />
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text3, marginTop: 4, textAlign: "center" }}>
                    Creating {progress.done} of {progress.total} prospects...
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(2)} disabled={importing} style={btnSecondary}>
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={importList.length === 0 || importing}
                  style={{
                    ...btnPrimary,
                    background: importList.length > 0 ? "#10b981" : "#6b7280",
                    opacity: importing ? 0.6 : 1,
                  }}
                >
                  {importing
                    ? `Importing ${progress.done}/${progress.total}...`
                    : `Import ${importList.length} Prospects`}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Done ───────────────────────────────── */}
          {step === 4 && importResult && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Check size={ICON_SIZES.xxl} style={{ color: "#10b981", marginBottom: 16 }} />
              <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, marginBottom: 12 }}>
                Prospect Import Complete
              </div>
              <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#10b981" }}>
                    {importResult.created}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>New prospects</div>
                </div>
                {importResult.linkedExisting > 0 && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#3b82f6" }}>
                      {importResult.linkedExisting}
                    </div>
                    <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Existing donors linked</div>
                  </div>
                )}
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700, color: palette.accent1 }}>
                    {importResult.recipientTotal}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Total recipients</div>
                </div>
                {importResult.errors > 0 && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#ef4444" }}>
                      {importResult.errors}
                    </div>
                    <div style={{ fontSize: "0.6rem", color: palette.text2 }}>Errors</div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3, marginBottom: 20, lineHeight: 1.5 }}>
                New prospects were added with the "Prospect" pathway.
                {importResult.linkedExisting > 0 &&
                  ` ${importResult.linkedExisting} existing donor${importResult.linkedExisting !== 1 ? "s were" : " was"} linked to this campaign without creating duplicates.`}
                {" "}The full segment is visible in the campaign recipient list.
              </div>
              <button onClick={onComplete} style={btnPrimary}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
